import { insertText } from "../taskpane";
import { getCommentedParagraphs } from "./getCommentedParagraphs";
import { getListOfParagraphs } from "./getListOfParagraphs";
import { getUserIdentifier } from "./getUserIdentifier";

const MIN_PARAGRAPH_LENGTH = 20;

export interface Correction {
  agent_name: string;
  rule: string;
  violation: string;
  correction: string;
  explanation: string;
  start: number;
  end: number;
  paragraph_num: number;
  edited_parag_start: number;
  edited_parag_end: number;
  type_of_input: 'main_text' | 'bullets' ;
}

export interface CorrectionsResponse {
  corrections: Correction[];
  edited_start_bullets?: string[];
  full_edited_text: string;
  edited_paragraphs: string[];
}

export interface ParagraphCorrection {
  corrected: string;
  text: string;
  index: number;
  paragraph: Word.Paragraph;
}
[];

async function pollForResults(
  apiUrl: string,
  jobId: string,
  abortControllerRef: React.MutableRefObject<AbortController>,
  intervalMs: number = 5000,
  maxAttempts: number = 60
): Promise<CorrectionsResponse> {
  const url = `${apiUrl}/corrections/result?job_id=${encodeURIComponent(jobId)}`;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (abortControllerRef.current.signal.aborted) {
      throw new Error("Polling aborted");
    }

    const response = await fetch(url, {
      method: "GET",
      signal: abortControllerRef.current.signal,
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status}`);
    }

    const result = await response.json();
    if (Array.isArray(result.edited_paragraphs)) {
      return result as CorrectionsResponse;
    }

    await new Promise((res) => setTimeout(res, intervalMs));
  }

  throw new Error("Polling timed out");
}

async function fetchCorrections({
  apiUrl,
  body,
  abortControllerRef,
  setJobId,
}: {
  apiUrl: string;
  body: object;
  abortControllerRef: React.MutableRefObject<AbortController>;
  setJobId: (loading: string) => void;
}): Promise<CorrectionsResponse> {
  // return require('../../../example_corrections.json');

  const endpoint = "corrections/get";
  const url = `${apiUrl}/${endpoint}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal: abortControllerRef.current.signal,
  });

  if (!response.ok) {
    throw new Error(`API call failed: ${response.status}`);
  }

  const result = await response.json();

  if (abortControllerRef.current.signal.aborted) {
    throw new Error("Polling aborted");
  }

  if (result.job_id) {
    setJobId(result.job_id);
    return pollForResults(apiUrl, result.job_id, abortControllerRef);
  }

  return result as CorrectionsResponse;
}

function filterParagraphs(
  paragraphs: Awaited<ReturnType<typeof getListOfParagraphs>>,
  printFilters: boolean
) {
  const pattern = /^(Source|Note|Figure|Fig)\.?\s?\d*\s?[.:]/i;
  const addFilter = (index, text, key) => {
    filteredOn[key].push(`${index} (${text})`);
    return false;
  };
  const filteredOn = {
    textExists: [],
    knownFont: [],
    minParagraphLength: [],
    listElement: [],
    sourceNoteFigureFig: [],
  };
  const filteredParagraphs = paragraphs
    .filter((p, index) => p.text || addFilter(index, p.text, "textExists"))
    .filter((p, index) => p.fontName || addFilter(index, p.text, "knownFont"))
    .filter(
      (p, index) =>
        p.text.length >= MIN_PARAGRAPH_LENGTH || addFilter(index, p.text, "minParagraphLength")
    )
    //.filter((p, index) => !p.isList || addFilter(index, p.text, 'listElement'))
    .filter(
      (p, index) => !pattern.test(p.text.trim()) || addFilter(index, p.text, "sourceNoteFigureFig")
    );

  if (printFilters) {
    insertText(JSON.stringify(filteredOn));
  }

  return filteredParagraphs;
}

function getBulletLists(
  paragraphs: Awaited<ReturnType<typeof getListOfParagraphs>>,
  startingParagraph: Awaited<ReturnType<typeof getListOfParagraphs>>[0]
) {
  const { starting, mainText } = paragraphs.reduce(
    (acc, paragraph) => {
      if (paragraph.isList) {
        if (acc.startingElementsEnded) {
          acc.mainText.push(paragraph);
        } else {
          acc.starting.push(paragraph);
        }
      } else {
        if (startingParagraph?.index !== paragraph.index && paragraph.text) {
          acc.startingElementsEnded = true;
        }
      }

      return acc;
    },
    { starting: [], mainText: [], startingElementsEnded: false }
  );

  return { starting, mainText };
}

export function getValidParagraphs(
  paragraphs: Awaited<ReturnType<typeof getListOfParagraphs>>,
  printFilters: boolean,
  publicationType: string,
) {
  let titleParagraph = paragraphs.find((p) => p.text) || {
    text: "",
    index: -1,
    end: 0,
    start: 0,
    isList: false,
    fontName: "",
  };
  const paragraphsWithoutTitle = paragraphs.filter(({ index }) => index !== titleParagraph.index);
  if (!paragraphsWithoutTitle.length) {
    paragraphsWithoutTitle.push({ ...titleParagraph });
    titleParagraph = { ...titleParagraph };
    titleParagraph.text = "";
  }

  const { starting } = publicationType === "Bullet-style report" ? { starting: [] } : getBulletLists(paragraphs, titleParagraph);
  const startingIndexes = starting.map((p) => p.index);
  const validParagraphs = filterParagraphs(
    paragraphsWithoutTitle.filter((p) => !startingIndexes.includes(p.index)),
    printFilters
  );

  return {
    validParagraphs,
    titleParagraph,
    starting,
  };
}

export async function getCorrections({
  printFilters,
  apiUrl,
  paragraphs,
  service,
  category,
  publicationType,
  abortControllerRef,
  applyHouseStyle,
  generalImprovement,
  skipRequest,
  setJobId,
}: {
  skipRequest: boolean;
  printFilters: boolean;
  apiUrl: string;
  paragraphs: Awaited<ReturnType<typeof getListOfParagraphs>>;
  service: string;
  category: string;
  publicationType?: string;
  applyHouseStyle: boolean;
  generalImprovement: boolean;
  abortControllerRef: React.MutableRefObject<AbortController>;
  setJobId: (loading: string) => void;
}) {
  let requestInfo = {};
  let responseInfo = {};
  const emptyResponse = {
    explanations: [],
    corrections: [],
    numberOfCorrections: 0,
    requestInfo,
    error: null,
    responseInfo,
  } as {
    explanations: Correction[];
    numberOfCorrections: number;
    error: Error | null;
    requestInfo: any;
    responseInfo: any;
    corrections: ParagraphCorrection[];
  };

  const { titleParagraph, validParagraphs, starting } = getValidParagraphs(
    paragraphs,
    printFilters,
    publicationType
  );

  const startingListElements = starting.map((p) => p.text).filter(Boolean) as string[];

  const body = {
    email: await getUserIdentifier(),
    title: titleParagraph.text,
    applyHouseStyle,
    generalImprovement,
    paragraphs: validParagraphs.map((p) => p.text),
    start_bullets: publicationType === "EA Live" ? [] : startingListElements,
    ...(service ? { service } : {}),
    ...(category ? { category } : {}),
    ...(publicationType ? { publication_type: publicationType } : {}),
  };

  requestInfo = { apiUrl, printFilters, ...body };
  
  if (skipRequest) {
    emptyResponse.requestInfo = requestInfo;
    return emptyResponse;
  }

  const responseBody = await fetchCorrections({
    apiUrl,
    body,
    abortControllerRef,
    setJobId,
  });
  responseInfo = responseBody;

  const editedParagraphs = responseBody?.edited_paragraphs;

  const validParagraphsCorrected = validParagraphs.map((p, index) => ({
    ...p,
    corrected: editedParagraphs[index],
  }));
  const corrections = validParagraphsCorrected.filter((c) => c.text && c.text !== c.corrected);

  const correctedStarting = (starting || []).map((p, index) => ({
    ...p,
    corrected: (responseBody.edited_start_bullets || [])[index],
  }));
  const correctionsStarting = correctedStarting.filter((c) => c.text && c.text !== c.corrected);

  return {
    explanations: responseBody.corrections
      .map((c) => {
        if (c.type_of_input === 'bullets') {
          const index = findStartingBulletIndex(startingListElements, c.start, c.end);
          c.paragraph_num = starting[index]?.index ?? -1;
        } else {
          const vp = validParagraphs[c.paragraph_num];
          c.paragraph_num = vp?.index ?? -1;
        }

        return c;
      })
      .filter((c) => c.paragraph_num !== -1),
    corrections: [...correctionsStarting, ...corrections],
    numberOfCorrections: (responseBody.corrections || []).length + (responseBody.edited_start_bullets || []).length,
    requestInfo,
    responseInfo,
    error: null,
  };
}

const findStartingBulletIndex = (arr, start, end) => {
  let currentPos = 0;

  return arr.findIndex(text => {
    const textStart = currentPos;
    const textEnd = currentPos + text.length;
    currentPos += text.length + 2;

    return start >= textStart && end <= textEnd;
  });
};

export async function filterCommentedCorrections(
  corrections: Awaited<ReturnType<typeof getCorrections>>
) {
  const commentedParagraphs = await getCommentedParagraphs();

  const correctionOldLength = corrections.corrections.length;
  corrections.corrections = corrections.corrections.filter(
    (c): c is ParagraphCorrection => !commentedParagraphs.includes(c.index)
  );
  const correctionNewLength = corrections.corrections.length;
  const filteredAmount = correctionOldLength - correctionNewLength;

  corrections.explanations = corrections.explanations.filter(
    (e) => !commentedParagraphs.includes(e.paragraph_num)
  );

  (corrections as any).filteredAmount = filteredAmount;
  return corrections;
}
